import React, { useState } from 'react';
import { Card, CardHeader, Heading, CardBody, useColorModeValue, Box, Text } from '@chakra-ui/react';
import ReactApexChart from 'react-apexcharts';
import { RotateCw } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { getCashFlowAnalysis } from '../../../../redux/actions/dashboard';

const CashFlowAnalysisChart = ({ data, title = 'Cash Flow Analysis', height = 450, financialAnalysisDateRange, user }) => {
   // Color mode values must be declared at the top
   const textColor = useColorModeValue('gray.800', 'white');
   const chartBg = useColorModeValue('white', 'gray.700');
   const gridColor = useColorModeValue('#E2E8F0', '#4A5568');
   const tooltipBg = useColorModeValue('#FFFFFF', '#2D3748');
   const tooltipTextColor = useColorModeValue('#1A202C', '#F7FAFC');
   const payablesColor = useColorModeValue('#E53E3E', '#FC8181'); // Red
   const receivablesColor = useColorModeValue('#38A169', '#68D391'); // Green
   const isDark = useColorModeValue(false, true);
   const [isRotating, setIsRotating] = useState(false);
   const dispatch = useDispatch();

   if (!data || data.length === 0) {
      return (
         <Card h={height} bg={chartBg}>
            <CardHeader>
               <Heading size="md" color={textColor}>
                  {title}
               </Heading>
            </CardHeader>
            <CardBody>
               <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                  <Text color={textColor}>No data available</Text>
               </Box>
            </CardBody>
         </Card>
      );
   }

   // Group by party, then build separate series for Payables & Receivables
   const receivables = data.filter((item) => item.type === 'RECEIVABLES');
   const payables = data.filter((item) => item.type === 'PAYABLES');

   // Unique sorted list of party names
   const allParties = Array.from(new Set(data.map((item) => item.party_name)));

   const getValue = (partyName, typeList) => {
      const match = typeList.find((item) => item.party_name === partyName);
      return match ? match.outstanding_amount : 0;
   };

   const getTooltipMap = (list) =>
      list.reduce((acc, item) => {
         acc[item.party_name] = {
            party_name: item.party_name,
            avg_days: item.avg_days_outstanding,
            count: item.transaction_count,
         };
         return acc;
      }, {});

   const tooltipPayables = getTooltipMap(payables);
   const tooltipReceivables = getTooltipMap(receivables);

   const options = {
      chart: {
         type: 'bar',
         stacked: false,
         toolbar: { show: false },
         background: chartBg,
         foreColor: textColor,
      },
      plotOptions: {
         bar: {
            horizontal: true,
            barHeight: '50%',
         },
      },
      dataLabels: {
         enabled: false,
      },
      xaxis: {
         title: {
            text: 'Outstanding Amount (₹)',
            style: {
               color: textColor,
            },
         },
         labels: {
            formatter: (val) => '₹' + val.toLocaleString('en-IN'),
            style: {
               colors: textColor,
            },
         },
         axisBorder: {
            color: gridColor,
         },
         axisTicks: {
            color: gridColor,
         },
      },
      yaxis: {
         categories: allParties,
         labels: {
            style: {
               colors: textColor,
            },
         },
      },
      tooltip: {
         shared: false,
         custom: ({ seriesIndex, dataPointIndex }) => {
            const name = allParties[dataPointIndex];
            const data = seriesIndex === 0 ? tooltipPayables[name] : tooltipReceivables[name];

            if (!data)
               return `<div style="padding:8px; background:${tooltipBg}; color:${tooltipTextColor}">No data</div>`;

            return `
          <div style="padding:8px; background:${tooltipBg}; color:${tooltipTextColor}; border-radius:4px">
            <strong>Party Name:</strong> ${data.party_name}<br/>
            <strong>Transactions:</strong> ${data.count}<br/>
            <strong>Avg Days:</strong> ${data.avg_days}
          </div>
        `;
         },
      },
      colors: [payablesColor, receivablesColor],
      legend: {
         position: 'top',
         labels: {
            colors: textColor,
         },
      },
      grid: {
         borderColor: gridColor,
         strokeDashArray: 4,
         xaxis: {
            lines: {
               show: true,
            },
         },
      },
   };

   const series = [
      {
         name: 'Payables',
         data: allParties.map((name) => getValue(name, payables)),
      },
      {
         name: 'Receivables',
         data: allParties.map((name) => getValue(name, receivables)),
      },
   ];

   const handleReload = async () => {
      setIsRotating(true);
      dispatch(
         getCashFlowAnalysis({
            companyId: user?.loginhistory?.companyId,
            fromDate: financialAnalysisDateRange.fromDate,
            toDate: financialAnalysisDateRange.toDate,
         }),
      );
      setTimeout(() => setIsRotating(false), 700);
   };

   return (
      <Card h={height} bg={chartBg}>
         <CardHeader display="flex">
            <Heading size="md" color={textColor}>
               {title}
            </Heading>
            <button
               onClick={handleReload}
               className={`ml-auto p-2 rounded-full transition-all hover:scale-110 ${isDark ? "hover:bg-gray-600 text-blue-400" : "hover:bg-gray-100 text-blue-600"
                  }`}
            >
               <RotateCw
                  className={`w-5 h-5 transform ${isRotating ? "rotate-[360deg]" : ""
                     } transition-transform ease-in-out`}
                  style={{
                     transitionDuration: isRotating ? "700ms" : "10ms",
                  }}
               />
            </button>
         </CardHeader>
         <CardBody>
            <ReactApexChart options={options} series={series} type="bar" height={height - 80} />
         </CardBody>
      </Card>
   );
};

export default CashFlowAnalysisChart;
